import { Platform } from 'react-native';

export type SpineAssetConfig = {
	skeletonFile: string;
	atlasFile: string;
	animations?: {
		idle?: string;
		hover?: string;
		press?: string;
		disabled?: string;
		[key: string]: string | undefined;
	};
};

/**
 * Resolves a Spine asset to the correct runtime path format per platform.
 * - web: files are served from `public/spine` via URL (`/spine/...`)
 * - iOS/Android: files are loaded from bundled app assets (`assets://spine/...`)
 */
const spinePath = (fileName: string): string =>
	Platform.OS === 'web' ? `/spine/${fileName}` : `assets://spine/${fileName}`;

/**
 * Central registry for Spine animation bundles used by the app.
 * Keep skeleton/atlas pairs together and expose canonical animation names
 * so components can reference them without hardcoded strings/paths.
 */
export const SPINE_ASSETS = {
	playNowHome: {
		skeletonFile: spinePath('home-page/playnow_button.json'),
		atlasFile: spinePath('home-page/playnow_button.atlas'),
		animations: {
			idle: 'IDLE',
		},
	},
	playNow: {
		skeletonFile: spinePath('home-page/UI_CTA_PLAYNOW.json'),
		atlasFile: spinePath('home-page/UI_CTA_MAIN_BUTTONS.atlas'),
		animations: {
			idle: 'IDLE',
			hover: 'HOVER',
			press: 'PRESS',
			disabled: 'DISABLED',
		},
	},
	vip: {
		skeletonFile: spinePath('home-page/UI_CTA_VIP.json'),
		atlasFile: spinePath('home-page/UI_CTA_MAIN_BUTTONS.atlas'),
		animations: {
			idle: 'IDLE',
			hover: 'HOVER',
			press: 'PRESS',
			disabled: 'DISABLED',
		},
	},
	tournaments: {
		skeletonFile: spinePath('home-page/UI_CTA_TOURNAMENTS.json'),
		atlasFile: spinePath('home-page/UI_CTA_MAIN_BUTTONS.atlas'),
		animations: {
			idle: 'IDLE',
			hover: 'HOVER',
			press: 'PRESS',
			disabled: 'DISABLED',
		},
	},
	cities: {
		skeletonFile: spinePath('cities/CITY.json'),
		atlasFile: spinePath('cities/CITY.atlas'),
		animations: {
			idle: 'IDLE',
			press: 'PRESS',
			disabled: 'OFF',
			hover: 'SELECT',
		},
	},
	logo: {
		skeletonFile: spinePath('logo/LOGO.json'),
		atlasFile: spinePath('logo/LOGO_ANIM.atlas'),
		animations: {
			idle: 'IDLE',
			enter: 'ENTER',
		},
	},
	loader: {
		skeletonFile: spinePath('loader/LOADER.json'),
		atlasFile: spinePath('loader/LOADER.atlas'),
		animations: {
			idle: 'vanish',
		},
	},
	dailyMissionClaim: {
		skeletonFile: spinePath('daily-missions/coin_claim_medium.json'),
		atlasFile: spinePath('daily-missions/coin_claim.atlas'),
		animations: {
			collect: 'collect',
		},
	},
} as const satisfies Record<string, SpineAssetConfig>;
